import React, { useState, useEffect } from "react";
import {
  Search,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
// Same stylesheet and class names (cml-*) as the Community Members page,
// so this page looks exactly the same.
import "../Styles/communitymember.css";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const API_BASE_URL = "";

// ---- Dummy data (used when the API is unreachable, so the UI can be
// previewed before the backend endpoints exist) ----


// Offline fallback so pagination still works visually during preview.



const partnerFields = [
  { name: "fullName", label: "Full Name", type: "text" },
  { name: "designation", label: "Designation", type: "text" },
  { name: "companyName", label: "Company", type: "text" },
  { name: "contactNumber", label: "Contact Number", type: "tel" },
  { name: "officialEmail", label: "Official Email", type: "email" },
  { name: "website", label: "Website", type: "url" },
  { name: "partnershipType", label: "Partnership Type", type: "text" },
  { name: "collaborateOn", label: "Collaboration", type: "text" },
];


export default function PartnerList() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [query, setQuery] = useState("");

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPartners, setTotalPartners] = useState(0);

  const [editingPartner, setEditingPartner] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Debounce search input so we're not hitting the DB on every keystroke
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Reset to page 1 whenever the page size or search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, debouncedQuery]);

 
const fetchPartners = async () => {
  try {
    setLoading(true);

    const params = new URLSearchParams({
      page: String(currentPage),
      limit: String(pageSize),
    });

    if (debouncedQuery) {
      params.set("search", debouncedQuery);
    }

    const res = await fetch(
      `${API_BASE_URL}/api/partners?${params.toString()}`
    );

    if (!res.ok) {
      throw new Error("Failed to load partners");
    }

    const data = await res.json();

    setPartners(data.partners || []);
    setTotalPages(data.totalPages || 1);
    setTotalPartners(data.total || 0);
    setError(null);
  } catch (err) {
    console.error("Fetch partners error:", err);

    setPartners([]);
    setTotalPages(1);
    setTotalPartners(0);
    setError("Failed to load partners. Please check the backend.");
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedQuery]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("dots-start");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("dots-end");
    pages.push(totalPages);
    return pages;
  };

  const toggleAll = () => {
    setSelectAll((v) => !v);
    setSelected(selectAll ? [] : partners.map((p) => p.id));
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Partner?",
      text: "Are you sure you want to delete this partner? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e0432c",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/partners/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      await fetchPartners();
      Swal.fire({
        title: "Deleted!",
        text: "The partner has been deleted.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "Failed to delete partner. Please try again.",
        icon: "error",
      });
    }
  };

 
const openEditModal = (p) => {
  setEditingPartner(p);

  setEditForm({
    fullName: p.fullName || "",
    designation: p.designation || "",
    companyName: p.companyName || "",
    contactNumber: p.contactNumber || "",
    officialEmail: p.officialEmail || "",
    website: p.website || "",
    partnershipType: p.partnershipType || "",
    collaborateOn: p.collaborateOn || "",
  });
};

  const closeEditModal = () => {
    setEditingPartner(null);
    setEditForm({});
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/partners/${editingPartner.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error("Failed to update partner");
      await fetchPartners();
      closeEditModal();
      
await fetchPartners();
closeEditModal();

Swal.fire({
  icon: "success",
  title: "Updated!",
  text: "Partner details updated successfully.",
  timer: 1500,
  showConfirmButton: false,
});

    } catch (err) {
      console.error(err);
      alert("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleExportToExcel = () => {
    if (partners.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Data",
        text: "There are no partners to export.",
      });
      return;
    }

   
const excelData = partners.map((p, index) => ({
  "S.No": (currentPage - 1) * pageSize + index + 1,
  "Name": p.fullName || "",
  "Designation": p.designation || "",
  "Company": p.companyName || "",
  "Contact": p.contactNumber || "",
  "Email": p.officialEmail || "",
  "Website": p.website || "",
  "Partnership Type": p.partnershipType || "",
  "Collaboration": p.collaborateOn || "",
}));


    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Partners");
    XLSX.writeFile(workbook, "partners.xlsx");
  };

  return (
    <div className="cml-app">
      {/* ---------------- Main content ---------------- */}
      <main className="cml-main">
        <section className="cml-panel">
          <div className="cml-controls">
            <label className="cml-show-entries">
              Show Entries :
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <span className="cml-pill cml-pill--total">Total Partners : {totalPartners}</span>

            <button className="cml-pill cml-pill--export" onClick={handleExportToExcel}>
              <span className="cml-excel-icon-wrap">
                <img
                  src="/assets/excel.png"
                  alt="Excel"
                  className="cml-excel-icon cml-excel-icon--default"
                />
                <img
                  src="/assets/excel-white.png"
                  alt="Excel"
                  className="cml-excel-icon cml-excel-icon--hover"
                />
              </span>
              Export To Excel
            </button>

            <div className="cml-search">
              <Search size={14} />
              <input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="cml-table-outer">
            <div className="cml-table-inner">
              {loading ? (
                <p style={{ padding: "20px" }}>Loading partners...</p>
              ) : error ? (
                <p style={{ padding: "20px", color: "#e0432c" }}>{error}</p>
              ) : (
                <table className="cml-table">
                  <thead>
                    <tr>
                      <th className="cml-checkbox-col">
                        <label className="cml-select-all">
                          <input type="checkbox" checked={selectAll} onChange={toggleAll} />
                          <span>Select All</span>
                        </label>
                      </th>
                      <th>S.No</th>
                      <th>Name</th>
                      <th>Designation</th>
                      <th>Company</th>
<th>Contact</th>
<th>Email</th>
<th>Website</th>
<th>Partnership Type</th>
<th>Collaboration</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ padding: "20px", textAlign: "center" }}>
  No partners yet.
</td>
                      </tr>
                    ) : (
                      partners.map((p, index) => (
                        <tr key={p.id}>
                          <td className="cml-checkbox-col">
                            <input
                              type="checkbox"
                              checked={selected.includes(p.id)}
                              onChange={() => toggleOne(p.id)}
                            />
                          </td>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>
                            <div className="cml-user-cell">
                              <span className="cml-avatar">{p.fullName?.charAt(0)}</span>
                              {p.fullName}
                            </div>
                          </td>
                          <td>{p.designation}</td>
                          <td>{p.companyName}</td>
<td>{p.contactNumber}</td>
<td>{p.officialEmail}</td>
<td>{p.website}</td>
<td>{p.partnershipType}</td>
<td>{p.collaborateOn}</td>
                          <td>
                            <div className="cml-row-actions">
                              <button
                                className="cml-icon-btn cml-icon-btn--edit"
                                onClick={() => openEditModal(p)}
                              >
                                <Pencil size={13} />
                                Edit
                              </button>
                              <button
                                className="cml-icon-btn cml-icon-btn--delete"
                                onClick={() => handleDelete(p.id)}
                              >
                                <Trash2 size={13} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="cml-table-footer">
            <span>
              Showing {partners.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
              &ndash;
              {(currentPage - 1) * pageSize + partners.length} of {totalPartners} Partners
            </span>
            <div className="cml-pagination">
              <button
                className="cml-page-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNumbers().map((p, i) =>
                p === "dots-start" || p === "dots-end" ? (
                  <span key={`${p}-${i}`} className="cml-page-dots">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`cml-page-btn${p === currentPage ? " active" : ""}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="cml-page-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* ---------------- Edit Modal ---------------- */}
      {editingPartner && (
        <div className="cml-modal-overlay" onClick={closeEditModal}>
          <div className="cml-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="cml-modal-title">Edit Partner</h2>
            <form onSubmit={handleEditSubmit} className="cml-modal-form">
              {partnerFields.map((f) => (
                <div className="cml-modal-field" key={f.name}>
                  <label>{f.label}</label>
                  <input
                    type={f.type}
                    name={f.name}
                    value={editForm[f.name] || ""}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              ))}
              <div className="cml-modal-actions">
                <button
                  type="button"
                  className="cml-modal-btn cml-modal-btn--cancel"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="cml-modal-btn cml-modal-btn--save"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}